/**
 * Shared background-pattern source of truth.
 *
 * The signature Vitness surface texture is a faint repeating mark that can light
 * up under the cursor (see `ShellBackground`, the marketing `Hero`, and
 * `AuthPage`). Each of those components owns its own opacity, masking, cursor
 * lighting, and accent glow — this module centralises *only* the piece that
 * decides what the repeating mark looks like: a classic dot, or the tiled
 * VITNESS logo. Switch the whole app via {@link PATTERN_DEFAULT}, or per-surface
 * where a component exposes a `pattern` prop.
 */

/** The two background pattern looks. */
export type PatternKind = 'logo' | 'dots';

/** App-wide default used when a caller doesn't pass an explicit pattern. */
export const PATTERN_DEFAULT: PatternKind = 'logo';

/** Grid spacing in px (matches the original dot-grid). */
export const PATTERN_TILE = 32;
/** Logo mark size in px within each cell — tiny; reads like a dot until zoomed. */
export const PATTERN_GLYPH = 4;
/** Optional rigid rotation (deg) applied to each mark. 0 = upright. */
export const PATTERN_ROTATE = 0;

/**
 * The VITNESS mark paths (mirrors `LogoMark`); native bbox viewBox "48 -8 624 530".
 * Kept as raw path data so the mark can be flattened into a tiled SVG.
 */
export const LOGO_MARK_PATHS = [
  'M241.571 100.464C244.367 90.1409 255.302 81.7727 265.995 81.7727H382.165C387.512 81.7727 390.712 85.9568 389.314 91.1182L299.44 422.882C298.041 428.043 292.574 432.227 287.227 432.227H171.057C160.364 432.227 153.963 423.859 156.759 413.536L241.571 100.464Z',
  'M83.4411 219.618C86.2375 209.295 97.173 200.927 107.866 200.927H175.632C180.978 200.927 184.179 205.111 182.781 210.273L157.464 303.727C154.668 314.05 143.732 322.418 133.039 322.418H74.9543C64.2611 322.418 57.8596 314.05 60.656 303.727L83.4411 219.618Z',
  'M555.715 191.582H652.523C657.869 191.582 661.07 195.766 659.672 200.927L642.499 264.32C632.408 301.568 592.95 331.764 554.365 331.764H517.74L555.715 191.582Z',
  'M462.402 0C451.709 0 440.774 8.36822 437.977 18.6909L308.862 495.309C306.065 505.632 312.467 514 323.16 514H449.01C459.703 514 470.639 505.632 473.435 495.309L555.715 191.582L502.156 191.582L562.073 168.113L605.083 9.34545C606.481 4.1841 603.28 0 597.933 0H462.402Z',
];

/**
 * Build a CSS `background-image` value for the repeating surface pattern.
 *
 * @param ink Fully-resolved mark colour (e.g. `rgba(255,255,255,0.06)`). The
 *   caller owns opacity, so every surface keeps its own treatment.
 * @param pattern Which look to render. Defaults to {@link PATTERN_DEFAULT}.
 * @returns A `radial-gradient(...)` (dots) or `url("data:image/svg+xml,...")`
 *   (logo), tiled at {@link PATTERN_TILE}px.
 */
export function patternImage(ink: string, pattern: PatternKind = PATTERN_DEFAULT): string {
  if (pattern === 'dots') return `radial-gradient(circle, ${ink} 1px, transparent 1px)`;
  const paths = LOGO_MARK_PATHS.map((d) => `<path d='${d}' fill='${ink}'/>`).join('');
  const c = PATTERN_TILE / 2; // cell centre
  const g = PATTERN_GLYPH;
  const rot = PATTERN_ROTATE ? ` transform='rotate(${PATTERN_ROTATE} ${c} ${c})'` : '';
  // One tiny mark per cell on a regular grid (the original dot-grid layout).
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${PATTERN_TILE}' height='${PATTERN_TILE}'>` +
    `<g${rot}><svg x='${c - g / 2}' y='${c - g / 2}' width='${g}' height='${g}' viewBox='48 -8 624 530'>${paths}</svg></g>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
